import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const API_BASE_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://food.api.j7lab.com';

const RECIPES_ENDPOINTS = [
  process.env.REACT_APP_PUBLIC_RECIPES_URL,
  `${API_BASE_URL}/api/public/recipes/latest?limit=100`,
  `${API_BASE_URL}/api/public/recipes?limit=100`,
];

const FALLBACK_IMAGE_URL = 'https://via.placeholder.com/640x400?text=Recette';

var CDN = process.env.NODE_ENV==='development' ? 'https://cdn.j7lab.com/dev' : 'https://cdn.j7lab.com';

const sanitizeImageBase = (value) => {
  if (!value) return null;
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const INGREDIENT_IMAGE_BASE = sanitizeImageBase(
  CDN
);

const extractStandardIngredientId = (ingredient) => {
  if (!ingredient) return null;
  const direct = ingredient.standardIngredientId || ingredient.standardIngredient;
  if (direct && typeof direct === 'string') return direct;
  if (direct && typeof direct === 'object' && direct._id) return direct._id;
  if (ingredient._id) return ingredient._id;
  if (ingredient.id) return ingredient.id;
  return null;
};

const buildIngredientImageUrl = (id, base) => {
  if (!id || !base) return null;
  return `${base}/ingredients/${id}.png`;
};

const getIngredientImageUrl = (ingredient) => {
  if (!ingredient) return null;

  const standardId = extractStandardIngredientId(ingredient);
  if (standardId) {
    const url = buildIngredientImageUrl(standardId, INGREDIENT_IMAGE_BASE);
    if (url) return url;
  }

  if (typeof ingredient.imageUrl === 'string' && ingredient.imageUrl.trim()) {
    return ingredient.imageUrl;
  }

  if (typeof ingredient.image === 'string' && ingredient.image.trim()) {
    return ingredient.image;
  }

  return null;
};

const normalizeRecipes = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.recipes)) return payload.recipes;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data?.recipes)) return payload.data.recipes;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.items)) return payload.data.items;
  return [];
};

const slugify = (value) => {
  if (!value) return 'recette';
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'recette';
};

const pickImage = (recipe) => {
  if (!recipe) return FALLBACK_IMAGE_URL;
  if (Array.isArray(recipe.imgUrlArray)) {
    const recipeImage = recipe.imgUrlArray.find((url) => typeof url === 'string' && url.includes('/recipe'));
    if (recipeImage) return recipeImage;
    const firstValid = recipe.imgUrlArray.find((url) => typeof url === 'string' && url.trim().length > 0);
    if (firstValid) return firstValid;
  }
  if (typeof recipe.imageUrl === 'string' && recipe.imageUrl.trim()) return recipe.imageUrl;
  if (typeof recipe.heroImage === 'string' && recipe.heroImage.trim()) return recipe.heroImage;
  if (typeof recipe.coverImage === 'string' && recipe.coverImage.trim()) return recipe.coverImage;
  return FALLBACK_IMAGE_URL;
};

const computeTotalTime = (recipe) => {
  const prep = Number(recipe?.prepTime || recipe?.preparationTime) || 0;
  const cook = Number(recipe?.cookTime || recipe?.bakingTime) || 0;
  const total = Number(recipe?.totalTime) || prep + cook;
  return total > 0 ? total : null;
};

const PublicRecipesListPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState(null);
  const [detailIngredients, setDetailIngredients] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const fetchRecipes = async () => {
      setStatus('loading');
      setErrorMessage(null);

      for (const endpoint of RECIPES_ENDPOINTS) {
        if (!endpoint) continue;
        try {
          const response = await fetch(endpoint, {
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
          });

          if (!response.ok) {
            continue;
          }

          const payload = await response.json();
          const normalized = normalizeRecipes(payload);

          if (!active) return;

          setRecipes(normalized);
          setStatus('success');
          return;
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Erreur lors du chargement des recettes publiques:', error);
        }
      }

      if (!active) return;

      setRecipes([]);
      setStatus('error');
      setErrorMessage('Impossible de charger les recettes pour le moment.');
    };

    fetchRecipes();

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const preparedRecipes = useMemo(() => {
    return (recipes || [])
      .map((recipe, index) => {
        const id = recipe._id;
        const slug = recipe.url || slugify(recipe.title || recipe.name || `recette-${index}`);
        const description = recipe.description || recipe.summary || '';
        const image = pickImage(recipe);
        const totalTime = computeTotalTime(recipe);
        const servings = recipe.servings || recipe.people || recipe.defaultServings;
        const ingredientsPreview = (Array.isArray(recipe.ingredients) ? recipe.ingredients : [])
          .map((ingredient, idx) => {
            if (!ingredient?.name) return null;
            const ingredientId = extractStandardIngredientId(ingredient);
            const ingredientSlug = slugify(ingredient.name);
            const quantity = [ingredient.quantity, ingredient.unity || ingredient.unit]
              .filter(Boolean)
              .join(' ');

            return {
              id: ingredientId,
              key: ingredientId || `${ingredientSlug}-${idx}`,
              slug: ingredientSlug,
              name: ingredient.name,
              image: getIngredientImageUrl(ingredient),
              quantity: quantity || null,
            };
          })
          .filter(Boolean)
          .slice(0, 4);

        return {
          id,
          slug,
          title: recipe.title || recipe.name || `Recette ${index + 1}`,
          description,
          image,
          totalTime,
          servings,
          complexity: recipe.complexity || recipe.difficulty || null,
          ingredients: ingredientsPreview,
        };
      })
      .filter((recipe) => recipe.id != null);
  }, [recipes]);

  useEffect(() => {
    let isMounted = true;

    const recipesNeedingDetails = preparedRecipes.filter(
      (recipe) => (!recipe.ingredients || recipe.ingredients.length === 0) && !detailIngredients[recipe.id]
    );

    if (recipesNeedingDetails.length === 0) {
      return () => {
        isMounted = false;
      };
    }

    const fetchDetails = async () => {
      const updates = {};

      await Promise.all(
        recipesNeedingDetails.map(async (recipe) => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/public/recipe/${recipe.id}`);
            if (!response.ok) return;

            const payload = await response.json();
            const detailedRecipe = payload?.data?.recipe || payload?.recipe || payload?.data || payload;
            if (!detailedRecipe || !Array.isArray(detailedRecipe.ingredients)) return;

            const preview = detailedRecipe.ingredients
              .map((ingredient, idx) => {
                if (!ingredient?.name) return null;
                const ingredientId = extractStandardIngredientId(ingredient);
                const ingredientSlug = slugify(ingredient.name);
                const quantity = [ingredient.quantity, ingredient.unity || ingredient.unit]
                  .filter(Boolean)
                  .join(' ');

                return {
                  id: ingredientId,
                  key: ingredientId || `${ingredientSlug}-${idx}`,
                  slug: ingredientSlug,
                  name: ingredient.name,
                  image: getIngredientImageUrl(ingredient),
                  quantity: quantity || null,
                };
              })
              .filter(Boolean)
              .slice(0, 4);

            if (preview.length > 0) {
              updates[recipe.id] = preview;
            }
          } catch (err) {
            console.error(`Erreur lors du chargement des ingrédients pour la recette ${recipe.id}:`, err);
          }
        })
      );

      if (isMounted && Object.keys(updates).length > 0) {
        setDetailIngredients((prev) => ({ ...prev, ...updates }));
      }
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [preparedRecipes, detailIngredients]);

  const recipesWithIngredients = useMemo(() => {
    if (Object.keys(detailIngredients).length === 0) return preparedRecipes;
    return preparedRecipes.map((recipe) => {
      if (recipe.ingredients && recipe.ingredients.length > 0) return recipe;
      const fromMap = detailIngredients[recipe.id];
      if (!fromMap || fromMap.length === 0) return recipe;
      return { ...recipe, ingredients: fromMap };
    });
  }, [preparedRecipes, detailIngredients]);

  // Extraire tous les ingrédients uniques des recettes (après recipesWithIngredients)
  const uniqueIngredients = useMemo(() => {
    const ingredientsMap = new Map();
    
    console.log('Analyzing recipes for ingredients:', recipesWithIngredients.length);
    
    recipesWithIngredients.forEach((recipe, recipeIndex) => {
      console.log(`Recipe ${recipeIndex}: ${recipe.title}`, recipe.ingredients?.length || 0, 'ingredients');
      recipe.ingredients?.forEach((ingredient, ingredientIndex) => {
        console.log(`  Ingredient ${ingredientIndex}:`, {
          id: ingredient.id,
          name: ingredient.name,
          slug: ingredient.slug
        });
        
        // Utiliser soit l'ID soit le slug comme clé unique
        const uniqueKey = ingredient.id || ingredient.slug || ingredient.name;
        if (ingredient.name && uniqueKey && !ingredientsMap.has(uniqueKey)) {
          ingredientsMap.set(uniqueKey, {
            id: ingredient.id || ingredient.slug,
            slug: ingredient.slug,
            name: ingredient.name
          });
        }
      });
    });
    
    const result = Array.from(ingredientsMap.values()).slice(0, 10);
    console.log('Unique ingredients found:', result);
    return result;
  }, [recipesWithIngredients]);

  const seo = (
    <Seo
      title="YAQAM - Liste des dernières idées de recettes générées par nos utilisateurs"
      description="Parcourez les dernières idées de recettes générées par YAQAM et trouvez votre prochaine inspiration d'idée repas."
      breadcrumb
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "YAQAM",
          "url": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com',
          "logo": "https://www.yaqam.com/img/logo_color-min.png",
          "contactPoint": [{
            "@type": "ContactPoint",
            "contactType": "customer support",
            "email": "info@j7lab.com"
          }]
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Accueil",
              "item": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com'
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Liste des dernières idées de recettes",
              "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + '/idees-de-repas'
            }
          ]
        }
      ]}
    />
  );

  return (
    <>
      {seo}
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#2C3E50' }}>
            Idées de recettes
          </h1>
          <h2 className="text-base md:text-lg max-w-3xl mx-auto" style={{ color: '#6c757d' }}>
            YAQAM génère des milliers d'idées de recettes par jour. Découvrez quelques recettes sur cette pages,revenez souvent pour en voir de nouvelles ou <a href="/register">inscrivez-vous pour utiliser YAQAM !</a>
          </h2>
        </header>

        {status === 'loading' && (
          <div className="flex justify-center py-20">
            <div className="h-12 w-12 rounded-full border-4 border-orange-200 border-t-[#E67E22] animate-spin"></div>
          </div>
        )}

        {status === 'error' && (
          <div className="max-w-md mx-auto bg-white border border-orange-200 rounded-2xl shadow-sm p-6 text-center" style={{ color: '#6c757d' }}>
            {errorMessage}
          </div>
        )}

        {status === 'success' && preparedRecipes.length === 0 && (
          <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm p-6 text-center" style={{ color: '#6c757d' }}>
            Aucune recette disponible pour le moment. Revenez bientôt !
          </div>
        )}
        <h3 className="text-2xl font-semibold mb-6" style={{ color: '#2C3E50' }}>
          Dernières idées de recettes générées pour nos utilisateurs :
        </h3>
        {recipesWithIngredients.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {recipesWithIngredients.map((recipe) => (
              <article
                key={recipe.id}
                className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col h-full transition-transform hover:-translate-y-1 hover:shadow-lg"
              >
                <Link to={`/idees-de-repas/idees-recette/${recipe.slug}/${recipe.id}`} className="block relative h-48">
                  <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity"></div>
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <Link to={`/idees-de-repas/idees-recette/${recipe.slug}/${recipe.id}`} className="text-lg font-semibold mb-2" style={{ color: '#2C3E50' }}>
                    {recipe.title}
                  </Link>

                  {recipe.ingredients?.length > 0 && (
                    <div className="mt-5 pt-5 border-t" style={{ borderColor: '#EAEFF2' }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#2C3E50' }}>
                        Ingrédients mis en avant
                      </p>
                      <ul className="space-y-3">
                        {recipe.ingredients.map((ingredient) => (
                          <li key={ingredient.key || `${ingredient.slug}`} className="flex items-center gap-3">
                            {ingredient.image ? (
                              <img
                                src={ingredient.image}
                                alt={ingredient.name}
                                className="h-10 w-10 rounded-full object-cover border border-gray-200 bg-gray-50"
                              />
                            ) : (
                              <span className="h-10 w-10 rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center text-sm" style={{ color: '#87A96B' }}>
                                🥗
                              </span>
                            )}
                            <div className="flex-1 min-w-0">
                              {ingredient.id ? (
                                <Link
                                  to={`/idees-de-repas/idees-recettes/${ingredient.slug}/${ingredient.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block text-sm font-semibold truncate"
                                  style={{ color: '#87A96B' }}
                                >
                                  {ingredient.name}
                                </Link>
                              ) : (
                                <span className="block text-sm font-semibold truncate" style={{ color: '#2C3E50' }}>
                                  {ingredient.name}
                                </span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
        
        {/* Section des idées de recettes par ingrédient */}
        {uniqueIngredients.length > 0 && (
          <div className="mt-12">
            <div className="space-y-6">
              {uniqueIngredients.map((ingredient) => (
                <h3 key={ingredient.id} className="text-2xl font-semibold mb-6" style={{ color: '#2C3E50' }}>
                  Idées de recettes à base de{' '}
                  <Link
                    to={`/idees-de-repas/idees-recettes/${ingredient.slug}/${ingredient.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors"
                    style={{ color: '#2C3E50' }}
                  >
                    {ingredient.name}
                  </Link>
                  {' '}:
                </h3>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default PublicRecipesListPage;
